import React from "react";
import { ShieldCheck, Phone, Heart } from "lucide-react";
import { PageHeader, Section, Card, Prose } from "../components/ui-kit";
import { useContact } from "../context/SiteSettingsContext";

const STEPS = [
  { n: "1", title: "Tell someone", text: "Speak to any member of staff, or contact us using the details on this page. We will listen." },
  { n: "2", title: "We take it seriously", text: "We treat every concern with care and respect, and act without delay where someone may be at risk." },
  { n: "3", title: "We work with you", text: "We keep you informed and involve the right people and partners as needed." },
];

export default function Safeguarding() {
  const CONTACT = useContact();
  return (
    <>
      <PageHeader
        eyebrow="Safeguarding and complaints"
        title="Everyone has the right to feel safe"
        intro="Safeguarding and wellbeing guide everything we do. We listen, we take concerns seriously, and we make it easy to raise anything that does not feel right."
        testid="safeguarding-header"
      />
      <Section tone="default">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          <Prose>
            <h2 className="font-heading text-3xl font-semibold text-stone-900">Our commitment</h2>
            <p>
              We are committed to protecting the safety, dignity and wellbeing of the people we
              support. We promote a culture where everyone feels able to speak up and be heard.
            </p>
            <p>
              If you have a concern about the safety or wellbeing of someone we support, please tell
              us straight away.
            </p>
          </Prose>
          <Card>
            <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
              <ShieldCheck className="h-6 w-6" aria-hidden="true" />
            </span>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-stone-900">Safeguarding contact</h2>
            <p className="mt-2 text-lg text-stone-700">Please use this contact for safeguarding matters:</p>
            <p className="mt-3 flex items-center gap-3 text-xl font-semibold text-stone-900">
              <Phone className="h-5 w-5 text-orange-700 shrink-0" aria-hidden="true" />
              {CONTACT.safeguarding}
            </p>
            <p className="mt-1 text-base text-stone-600">{CONTACT.safeguardingHours}</p>
            <div className="mt-4 rounded-xl bg-stone-50 p-4">
              <p className="text-sm font-semibold uppercase tracking-wide text-orange-700">Out of hours</p>
              <p className="mt-1 text-lg font-medium text-stone-900">{CONTACT.safeguardingOOH}</p>
            </div>
            <p className="mt-4 text-base text-stone-600">
              If someone is in immediate danger, always contact the emergency services first.
            </p>
          </Card>
        </div>
      </Section>

      <Section tone="white">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Making a complaint</h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            We welcome feedback, including when something has gone wrong. Telling us helps us put
            things right and improve. You can raise a concern in the way that feels easiest for you.
          </p>
        </div>
        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {STEPS.map((s) => (
            <Card key={s.n} data-testid={`complaint-step-${s.n}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-600 text-white font-heading font-bold text-xl">
                {s.n}
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold text-stone-900">{s.title}</h3>
              <p className="mt-2 text-lg text-stone-700 leading-relaxed">{s.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="tinted">
        <div className="max-w-3xl">
          <Card>
            <Heart className="h-7 w-7 text-orange-700" aria-hidden="true" />
            <h2 className="mt-4 font-heading text-xl font-semibold text-stone-900">Wellbeing</h2>
            <p className="mt-2 text-lg text-stone-700">
              We support emotional as well as physical wellbeing, and create a calm, respectful
              environment for everyone.
            </p>
          </Card>
        </div>
      </Section>
    </>
  );
}
